import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { API_PATHS } from 'constants/apiPaths';
import {
    BasicItemResponse,
    BasicItemsResponse,
    ContainerType,
    OptimizationTaskPanel,
    OptimizationTaskStatus,
    OptimizationTaskWithPanels,
} from 'types';
import { useAuth } from 'utils/useAuth';

type ResponseType = BasicItemResponse<OptimizationTaskWithPanels>;

export const useOptimizationTask = (select?: (data: ResponseType) => ResponseType) => {
    const { token } = useAuth();
    const params = useParams();
    const taskId = params.taskId || '';

    return useQuery<ResponseType>({        
        queryKey: ['optimization-task', taskId, token],

        queryFn: async () => {
            const taskResponse = await findTasks(taskId, token);

            if (!taskResponse.isSuccess) return taskResponse;

            const optimizationTask = taskResponse.item;

            const panelsResponse = await findTaskPanels(taskId, token);

            if (!panelsResponse.isSuccess) return { ...panelsResponse, item: null };

            optimizationTask.panels = panelsResponse.items;

            const containersResponse = await findContainerTypes(token);

            if (!containersResponse.isSuccess) return { ...containersResponse, item: null };

            optimizationTask.containerType = containersResponse.items.find(
                (x) => x.id === optimizationTask.containerTypeId,
            )!;

            return { isSuccess: true, item: optimizationTask, message: null };
        },
        enabled: !!token && !!taskId,
        select: select,
        refetchInterval: (query) => {
            const previousResponse = query.state.data;

            if (!previousResponse?.isSuccess) return 10000;

            return refetchIntervals[previousResponse.item.status];
        },
    });
};

async function findTasks(taskId: string, token: string | null): Promise<BasicItemResponse<OptimizationTaskWithPanels>> {
    try {

        const response = await axios.get<BasicItemResponse<OptimizationTaskWithPanels>>(API_PATHS.optimizationTask.replace(':taskId', taskId), {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;

    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.message)
            return e.response.data;

        return { isSuccess: false, message: "Server error. Repeat operation later", item: null }
    }
}

async function findTaskPanels(taskId: string, token: string | null): Promise<BasicItemsResponse<OptimizationTaskPanel>> {
    try {
        const response = await axios.get<BasicItemsResponse<OptimizationTaskPanel>>(
            API_PATHS.optimizationTaskPanels.replace(':taskId', taskId),
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.message)
            return e.response.data;

        return { isSuccess: false, message: "Server error. Repeat operation later", items: null }
    }
}

async function findContainerTypes(token: string | null): Promise<BasicItemsResponse<ContainerType>> {
    try {
        const response = await axios.get<BasicItemsResponse<ContainerType>>(API_PATHS.containerTypes, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    
        return response.data;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.message)
            return e.response.data;

        return { isSuccess: false, message: "Server error. Repeat operation later", items: null }
    }
}

type RefetchIntervals = {
    [key in OptimizationTaskStatus]?: number;
};

const refetchIntervals: RefetchIntervals = {
    Created: 100,
    Queued: 700,
    'In progress': 30000,
    Optimized: 1000,
};
